/**
 * ErrorFallback — Premium error boundary display.
 */

import { useRouter } from "@tanstack/react-router";
import { AlertTriangle } from "lucide-react";

type ErrorFallbackProps = {
  error: Error;
  reset: () => void;
};

export function ErrorFallback({ error, reset }: ErrorFallbackProps) {
  const router = useRouter();

  if (import.meta.env.DEV) {
    console.error(error);
  }

  return (
    <div className="flex min-h-[80vh] items-center justify-center px-5">
      <div className="max-w-md text-center">
        <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-destructive/10">
          <AlertTriangle className="h-7 w-7 text-destructive" strokeWidth={1.25} />
        </div>
        <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
          We hit an unexpected issue loading this page. Please try again or head back home.
        </p>
        {error?.message && (
          <div className="mt-4 p-3 bg-destructive/10 border border-destructive/20 text-destructive text-xs font-mono text-left rounded-lg overflow-x-auto max-h-40">
            <strong>Error:</strong> {error.message}
            {error.stack && (
              <pre className="mt-2 text-[10px] text-muted-foreground whitespace-pre-wrap">
                {error.stack}
              </pre>
            )}
          </div>
        )}

        <div className="mt-8 flex flex-wrap justify-center gap-3">
          <button
            onClick={() => {
              router.invalidate();
              reset();
            }}
            className="inline-flex min-h-12 items-center justify-center bg-primary px-8 text-xs tracking-[0.2em] text-primary-foreground uppercase transition-opacity hover:opacity-90"
          >
            Try again
          </button>
          <a
            href="/"
            className="inline-flex min-h-12 items-center justify-center border border-border px-8 text-xs tracking-[0.2em] uppercase transition-colors hover:bg-secondary"
          >
            Go home
          </a>
        </div>
      </div>
    </div>
  );
}
